import type { ThemeState } from '@atlaskit/tokens/theme-config';
import { getGlobalTheme } from '@atlaskit/tokens/get-global-theme';
import { getThemeHtmlAttrs } from '@atlaskit/tokens/get-theme-html-attrs';
import { getThemeStyles } from '@atlaskit/tokens/get-theme-styles';
import { ThemeMutationObserver } from '@atlaskit/tokens/theme-mutation-observer';

interface IframeThemeInfo {
	themeHtmlAttrs: Record<string, string>;
	styleElements: HTMLStyleElement[];
}

interface IframeThemeListener {
	onIframeClose: () => void;
}

const buildStyleElements = async (
	activeTheme: Partial<ThemeState>,
): Promise<HTMLStyleElement[]> => {
	return (await getThemeStyles(activeTheme)).map((theme) => {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const styleElement: HTMLStyleElement = document.createElement('style');
		styleElement.innerHTML = theme.css;
		Object.entries(theme.attrs).forEach(([key, value]) => styleElement.setAttribute(key, value));
		return styleElement;
	});
};

// Runs in the **parent** window. Passes theme info into a child iframe via
// `onThemeUpdated` and keeps it in sync. When `iframeWindow` is provided, a
// MutationObserver auto-disconnects the theme listener once the iframe is
// removed from the DOM, preventing same-origin closure leaks.
const initializeIframeTheming = async (
	onThemeUpdated: (iframeThemeInfo: IframeThemeInfo) => void,
	iframeWindow?: Window,
): Promise<IframeThemeInfo & IframeThemeListener> => {
	const activeTheme = getGlobalTheme();

	const themeObserver = new ThemeMutationObserver(async (newTheme: Partial<ThemeState>) => {
		onThemeUpdated({
			themeHtmlAttrs: getThemeHtmlAttrs(newTheme),
			styleElements: await buildStyleElements(newTheme),
		});
	});

	themeObserver.observe();

	const teardown = () => {
		themeObserver.disconnect();
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		domObserver?.disconnect();
	};

	// Auto-disconnect when the iframe is removed from the DOM to prevent
	// same-origin memory leaks (closures over the iframe's JS context).
	let domObserver: MutationObserver | undefined;
	if (iframeWindow) {
		try {
			const iframeElement = iframeWindow.frameElement;
			if (iframeElement && iframeElement.parentNode) {
				domObserver = new MutationObserver((mutations) => {
					for (const mutation of mutations) {
						for (const removedNode of Array.from(mutation.removedNodes)) {
							if (removedNode === iframeElement || removedNode.contains(iframeElement)) {
								teardown();
								return;
							}
						}
					}
				});
				// Observe the document body for child removals (subtree covers
				// cases where a parent wrapper is removed rather than the
				// iframe directly).
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				domObserver.observe(document.body, { childList: true, subtree: true });
			}
		} catch {
			// Guard against cross-origin frameElement access.
		}
	}

	return {
		themeHtmlAttrs: getThemeHtmlAttrs(activeTheme),
		styleElements: await buildStyleElements(activeTheme),
		onIframeClose: teardown,
	};
};

// Called from inside an iframe. Passes the iframe's `window` to the parent
// so it can auto-disconnect when the iframe is removed from the DOM.
export const setupThemeInsideIframe = (
	onThemeUpdated: (iframeThemeInfo: IframeThemeInfo) => void,
): void => {
	if (window.parent.initializeIframeTheming) {
		void window.parent
			.initializeIframeTheming(onThemeUpdated, window)
			.then((iframeThemeInitInfo: IframeThemeInfo & IframeThemeListener) => {
				onThemeUpdated(iframeThemeInitInfo);
				// Safety-net for iframe navigation (not just DOM removal).
				window.addEventListener('beforeunload', iframeThemeInitInfo.onIframeClose);
			});
	}
};

export const applyThemeInsideIframe = (iframeThemeInfo: IframeThemeInfo, doc: Document): void => {
	// Add HTMl attributes which trigger styles to take effect
	Object.entries(iframeThemeInfo.themeHtmlAttrs).forEach(([key, value]) =>
		doc.documentElement.setAttribute(key, value),
	);

	// Add theming stylesheet(s) into page
	iframeThemeInfo.styleElements.forEach((styleElement: HTMLStyleElement) => {
		const existingStyleElement = doc.head.querySelector(
			`style[data-theme="${styleElement.getAttribute('data-theme')}"]`,
		);
		if (!existingStyleElement) {
			// Prevent re-inserting the same style element into the DOM repeatedly
			doc.head.appendChild(styleElement);
		}
	});
};

export const addIframeThemingSupport = (): void => {
	window.initializeIframeTheming = initializeIframeTheming;
};
